(() => {
  const element = document.getElementById('CFMarketBenefits');
  if (!element) return;
  const config = JSON.parse(element.textContent);
  const currency = config.currency;
  const shopCurrency = window.Shopify?.currency;
  const rate = currency === config.baseCurrency ? 1 :
    shopCurrency?.active === currency ? Number(shopCurrency.rate) : NaN;
  const validRate = Number.isFinite(rate) && rate > 0;
  const convert = amount => validRate && Number.isFinite(Number(amount))
    ? Math.round(Number(amount) * rate) : null;
  const shipping = config.shippingSupported
    ? convert(config.shippingThreshold) : null;
  const discount = convert(config.discountThreshold);
  const moneyLocale = { GBP: 'en-GB', USD: 'en-US', CAD: 'en-CA', CHF: 'de-CH' }[currency] || config.locale;
  const money = cents => new Intl.NumberFormat(moneyLocale, {
    style: 'currency', currency,
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2
  }).format(cents / 100);
  const text = (key, amount) => config.text[key].replace('[amount]', money(amount));
  const calculate = (shippingTotal, discountTotal) => ({
    shippingRemaining: shipping === null ? null : Math.max(0, shipping - shippingTotal),
    discountRemaining: discount === null ? null : Math.max(0, discount - discountTotal)
  });

  class MarketMessage extends HTMLElement {
    connectedCallback() {
      const isDiscount = this.dataset.kind === 'discount';
      const value = isDiscount ? discount : shipping;
      if (value === null || value <= 0) {
        this.textContent = config.text[isDiscount ? 'discountFallback' : 'shippingFallback'];
        return;
      }
      this.textContent = text(isDiscount ? 'discountFrom' :
        this.dataset.kind === 'shipping-minimum' ? 'from' : 'shippingFrom', value);
      this.dataset.currency = currency;
      this.dataset.threshold = String(value);
    }
  }

  class MarketRewards extends HTMLElement {
    connectedCallback() {
      const shippingTotal = Number(this.dataset.shippingTotal);
      const discountTotal = Number(this.dataset.discountTotal);
      if (!Number.isFinite(shippingTotal) || !Number.isFinite(discountTotal)) return;
      const hasShipping = shipping !== null && shipping > 0 && this.dataset.requiresShipping !== 'false';
      const hasDiscount = discount !== null && discount > 0 && this.dataset.secondReward === 'true';
      if (!hasShipping && !hasDiscount) return;
      const remaining = calculate(shippingTotal, discountTotal);
      this.replaceChildren();
      this.dataset.currency = currency;
      this.dataset.rate = String(rate);
      const make = (tag, className, content) => {
        const node = document.createElement(tag);
        node.className = className;
        if (content) node.textContent = content;
        return node;
      };
      this.append(make('strong', 'cf-sidecart__shipping-title', this.dataset.title));
      const message = make('div', 'cf-sidecart__shipping-message');
      const copy = make('div', 'cf-sidecart__shipping-copy');
      message.append(copy);
      const next = (key, amount) => {
        const line = make('span', 'cf-sidecart__shipping-next');
        const [before, after] = config.text[key].split('[amount]');
        line.append(document.createTextNode(before), make('strong', '', money(amount)), document.createTextNode(after || ''));
        copy.append(line);
      };
      const success = key => copy.append(make('span', 'cf-sidecart__shipping-success cf-sidecart__reward-success', `✓ ${config.text[key]}`));
      if (hasShipping && remaining.shippingRemaining > 0) next('shippingRemaining', remaining.shippingRemaining);
      else {
        if (hasShipping) success('shippingReached');
        if (hasDiscount && remaining.discountRemaining > 0) next('discountRemaining', remaining.discountRemaining);
        else if (hasDiscount) success(this.dataset.discountApplied === 'true' ? 'discountApplied' : 'discountReached');
      }
      this.append(message);
      const twoSteps = hasShipping && hasDiscount && discount > shipping;
      const goal = hasDiscount ? discount : shipping;
      let progress;
      if (twoSteps) {
        progress = remaining.shippingRemaining > 0 ? Math.min(shippingTotal, shipping) / goal * 100
          : Math.max(shipping, Math.min(discountTotal, discount)) / goal * 100;
      } else progress = (hasDiscount ? discountTotal : shippingTotal) / goal * 100;
      progress = Math.min(100, Math.max(0, progress));
      const bar = make('div', `cf-sidecart__progress${twoSteps ? ' cf-sidecart__progress--rewards' : ''}`);
      bar.setAttribute('role', 'progressbar');
      bar.setAttribute('aria-label', config.text.progress);
      bar.setAttribute('aria-valuemin', '0');
      bar.setAttribute('aria-valuemax', '100');
      bar.setAttribute('aria-valuenow', String(Math.round(progress)));
      const fill = document.createElement('span');
      fill.style.setProperty('--cf-progress', `${progress}%`);
      bar.append(fill);
      if (twoSteps) {
        bar.style.setProperty('--cf-shipping-marker', `${shipping / goal * 100}%`);
        const marker = document.createElement('i');
        marker.setAttribute('aria-hidden', 'true');
        bar.append(marker);
      }
      this.append(bar);
      const labels = make('div', 'cf-sidecart__reward-labels');
      const label = (key, value, reached) => labels.append(make('span', reached ? 'is-reached' : '', `${reached ? '✓ ' : ''}${text(key, value)}`));
      if (hasShipping) label('shippingFrom', shipping, remaining.shippingRemaining === 0);
      if (hasDiscount) label('discountFrom', discount, remaining.discountRemaining === 0);
      this.append(labels);
    }
  }
  window.CFMarketBenefits = Object.freeze({ currency, rate, shipping, discount, calculate });
  if (!customElements.get('cf-market-message')) customElements.define('cf-market-message', MarketMessage);
  if (!customElements.get('cf-market-rewards')) customElements.define('cf-market-rewards', MarketRewards);
})();
